// ./src/contexts/AccountPreferenceContext/AccountPreferenceContext.tsx
//
// Thin context wrapper around `useAccountPreference` (ROADMAP A2b #53).
//
// ⚠⚠ MOUNTED APP-WIDE ON PURPOSE, AND IT HAS TWO CONSUMERS THAT MUST AGREE: the Event Defaults page
// that edits it, and the Create Event wizard that STARTS FROM it. The wizard is reached from
// anywhere, so a provider mounted at the page would leave it with nothing to seed from — which is
// exactly the failure the item is about (a preferences page nothing consumes).
//
// ⓘ It takes the app-wide slot that `UserProfileProvider` used to occupy. That one polled
// `userProfileList` — **carexpenses-api's** query, never part of this product's supergraph — on every
// signed-in session.

import React, { createContext, useContext } from "react";

import { useAccountPreference, UseAccountPreferenceOptions, UseAccountPreferenceReturn } from "@/hooks/useAccountPreference";

const AccountPreferenceContext = createContext<UseAccountPreferenceReturn | null>(null);

export interface AccountPreferenceProviderProps extends UseAccountPreferenceOptions {
  children: React.ReactNode;
}

export const AccountPreferenceProvider: React.FC<AccountPreferenceProviderProps> = ({ children, ...options }) => {
  const value = useAccountPreference(options);

  return <AccountPreferenceContext.Provider value={value}>{children}</AccountPreferenceContext.Provider>;
};

export const useAccountPreferenceContext = (): UseAccountPreferenceReturn => {
  const context = useContext(AccountPreferenceContext);

  if (!context) {
    throw new Error(
      "useAccountPreferenceContext must be used within an <AccountPreferenceProvider>. " +
        "Wrap your component tree with <AccountPreferenceProvider> to fix this."
    );
  }

  return context;
};

/** Null-tolerant read — the wizard should seed what it can, never crash when the provider is absent. */
export const useOptionalAccountPreferenceContext = (): UseAccountPreferenceReturn | null => {
  return useContext(AccountPreferenceContext);
};
